"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";

const API_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001/api/v1";

type Completeness = { filled: number; total: number };

export default function ProfileBanner() {
  const { user, loading: authLoading } = useAuth();
  const [completeness, setCompleteness] = useState<Completeness | null>(null);

  useEffect(() => {
    if (authLoading || !user) return;

    let cancelled = false;

    (async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token || cancelled) return;

      try {
        const res = await fetch(`${API_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const json = await res.json();
        if (!cancelled) setCompleteness(json.profile.completeness);
      } catch {
        // 取得失敗時はバナーを表示しない
      }
    })();

    return () => { cancelled = true; };
  }, [user, authLoading]);

  // 未ログイン、読み込み中、完了済み（3/3）の場合は非表示
  if (!completeness || completeness.filled >= completeness.total) return null;

  const percentage = completeness.total > 0 ? Math.round((completeness.filled / completeness.total) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto px-4 pt-6">
      <div className="bg-surface-container-low rounded-2xl p-5">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-subtext font-medium">プロフィール完成度</span>
            <span className="text-on-surface font-bold">{completeness.filled}/{completeness.total}</span>
          </div>
          <div className="h-2 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all duration-500"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <p className="text-sm text-subtext">
            プロフィール設定でフィードバックの精度が上がります
          </p>
          <Link
            href="/profile"
            className="text-sm font-bold text-blue-800 hover:text-blue-900 transition-colors whitespace-nowrap ml-4"
          >
            設定する →
          </Link>
        </div>
      </div>
    </div>
  );
}
